import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Icon from '../../../components/AppIcon';
import api, { agentServiceApi } from '../../../api/axios';
import AgentTestChatScreen from './AgentTestChatScreen';
import { EnterpriseContactPopup } from './AssistantCreationFlow';

// ─── Template Library Data ───────────────────────────────────────────────────
const PROMPT_TEMPLATES = [
  { industry: 'Ecommerce', title: 'Cart Recovery Agent', prompt: 'You are a friendly cart recovery specialist for an ecommerce store. Your goal is to re-engage customers who left items in their cart. Remind them of the items, offer assistance, and highlight any ongoing promotions or discounts to encourage them to complete their purchase.' },
  { industry: 'Ecommerce', title: 'Order Confirmation Agent', prompt: 'You are an order confirmation agent. Your role is to confirm recent orders with customers, provide estimated delivery timelines, and answer any questions about their purchase. Be concise, professional, and reassuring.' },
  { industry: 'Ecommerce', title: 'Refund Assistance Agent', prompt: 'You are a refund and returns specialist. Help customers initiate return requests, explain the refund policy clearly, and guide them through the process step by step. Be empathetic and solution-oriented.' },
  { industry: 'BFSI', title: 'EMI Reminder Agent', prompt: 'You are a polite EMI reminder agent for a financial institution. Remind customers about upcoming or overdue EMI payments, provide payment options, and answer queries about their loan account. Maintain a professional and non-threatening tone.' },
  { industry: 'BFSI', title: 'Insurance Renewal Agent', prompt: 'You are an insurance renewal specialist. Contact customers whose policies are due for renewal, explain the benefits of renewing, and guide them through the renewal process. Highlight any new features or improved coverage options.' },
  { industry: 'BFSI', title: 'Loan Collection Agent', prompt: 'You are a loan collection agent. Your role is to contact customers with overdue loan payments, understand their situation empathetically, and work out a feasible repayment plan. Always maintain a respectful and professional tone.' },
  { industry: 'Logistics', title: 'Delivery Confirmation Agent', prompt: 'You are a delivery confirmation agent for a logistics company. Confirm successful deliveries with customers, collect delivery feedback, and handle any delivery-related issues or complaints promptly and professionally.' },
  { industry: 'Logistics', title: 'Shipping Update Agent', prompt: 'You are a proactive shipping update agent. Inform customers about the current status of their shipments, expected delivery windows, and any delays. Provide tracking information and escalate issues when necessary.' },
  { industry: 'Logistics', title: 'NDR Resolution Agent', prompt: 'You are an NDR (Non-Delivery Report) resolution agent. Contact customers to reschedule failed deliveries, verify delivery addresses, and coordinate convenient delivery windows. Be efficient and customer-friendly.' },
  { industry: 'Medical', title: 'Appointment Reminder Agent', prompt: 'You are a healthcare appointment reminder agent. Remind patients of upcoming appointments, confirm attendance, provide pre-appointment instructions, and offer rescheduling options if needed. Maintain a caring and professional tone.' },
  { industry: 'Medical', title: 'Medication Follow-up Agent', prompt: 'You are a medication adherence follow-up agent for a healthcare provider. Check in with patients about their medication schedule, address any concerns, and remind them of the importance of completing their prescribed course.' },
  { industry: 'Education', title: 'Education Counselling Agent', prompt: 'You are an education counselling agent. Help prospective students understand available courses, admission requirements, and career prospects. Answer queries about fees, scholarships, and campus facilities in a friendly and informative manner.' },
  { industry: 'Education', title: 'Fee Reminder Agent', prompt: 'You are a student fee reminder agent for an educational institution. Remind students or parents about upcoming fee deadlines, explain payment options, and assist with any fee-related queries. Be polite and helpful.' },
  { industry: 'Recruitment', title: 'Interview Scheduler Agent', prompt: 'You are an interview scheduling agent for a recruitment firm. Contact candidates to schedule interviews, confirm availability, share interview details, and answer basic questions about the role and company. Be professional and encouraging.' },
  { industry: 'Recruitment', title: 'Candidate Follow-up Agent', prompt: 'You are a candidate follow-up agent. Reach out to candidates after interviews to gather feedback, communicate next steps, and maintain engagement throughout the hiring process. Be transparent and timely.' },
  { industry: 'Professional Services', title: 'Demo Booking Agent', prompt: 'You are a demo booking agent for a SaaS company. Engage with prospects to schedule product demonstrations, qualify their requirements, and ensure they are connected with the right sales representative. Be enthusiastic and knowledgeable.' },
  { industry: 'Professional Services', title: 'Lead Qualification Agent', prompt: 'You are a lead qualification agent. Engage with inbound leads to understand their needs, budget, and timeline. Qualify them based on predefined criteria and route hot leads to the sales team promptly.' },
  { industry: 'Professional Services', title: 'Feedback Collection Agent', prompt: 'You are a customer feedback collection agent. Reach out to customers post-service to gather structured feedback, understand satisfaction levels, and identify areas for improvement. Be concise and appreciative of their time.' },
  { industry: 'General', title: 'Customer Support Agent', prompt: 'You are a helpful customer support agent. Your goal is to resolve customer queries efficiently and empathetically. Listen carefully, provide accurate information, and escalate complex issues to human agents when necessary. Always maintain a positive and professional tone.' },
  { industry: 'General', title: 'Welcome Call Agent', prompt: 'You are a welcome call agent for a new customer onboarding program. Greet new customers warmly, introduce them to key product features, answer initial questions, and set expectations for their journey. Make them feel valued and excited.' },
];

const INDUSTRIES = ['All', 'Ecommerce', 'BFSI', 'Logistics', 'Medical', 'Education', 'Recruitment', 'Professional Services', 'General'];

const KB_SECTIONS = [
  { id: 'shipping', label: 'Shipping Info', icon: 'Truck' },
  { id: 'product', label: 'Product Info', icon: 'Package' },
  { id: 'company', label: 'About Company', icon: 'Building2' },
  { id: 'refund', label: 'Return / Refund Policy', icon: 'RotateCcw' },
  { id: 'contact', label: 'Contact Us', icon: 'Phone' },
];

// ─── Reusable Slider Component ────────────────────────────────────────────────
const SliderField = ({ label, min, max, step = 1, value, onChange, unit = '', description = null }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-md">
          {value}{unit}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e?.target?.value))}
          className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
          style={{ background: `linear-gradient(to right, var(--color-primary) ${percentage}%, var(--color-muted) ${percentage}%)` }}
        />
        <div className="flex justify-between mt-1">
          <span className="text-xs text-muted-foreground">{min}{unit}</span>
          <span className="text-xs text-muted-foreground">{max}{unit}</span>
        </div>
      </div>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
};

// ─── Toggle Component ─────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, label, description }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1">
      {label && <p className="text-sm font-medium text-foreground">{label}</p>}
      {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 flex-shrink-0 ${checked ? 'bg-primary' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
);

// ─── Section Card ─────────────────────────────────────────────────────────────
const SectionCard = ({ title, children, headerRight = null }) => (
  <div className="bg-card border border-border rounded-xl p-5 space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {headerRight}
    </div>
    {children}
  </div>
);

// ─── Textarea Field ───────────────────────────────────────────────────────────
const TextareaField = ({ label, value, onChange, placeholder, rows = 4 }) => (
  <div className="space-y-1.5">
    {label && <label className="text-sm font-medium text-foreground">{label}</label>}
    <textarea
      value={value}
      onChange={(e) => onChange(e?.target?.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none transition-colors"
    />
  </div>
);

// ─── Select Field ─────────────────────────────────────────────────────────────
const SelectField = ({ label, value, onChange, options }) => (
  <div className="space-y-1.5">
    {label && <label className="text-sm font-medium text-foreground">{label}</label>}
    <select
      value={value}
      onChange={(e) => onChange(e?.target?.value)}
      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors appearance-none cursor-pointer"
    >
      {options?.map((opt) => (
        <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
      ))}
    </select>
  </div>
);

// ─── Provider Select (custom dropdown with enterprise badges) ─────────────────
const ProviderSelect = ({ label, value, onChange, options, onEnterprise }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options?.find(o => o.value === value);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="space-y-1.5 relative" ref={ref}>
      {label && <label className="text-sm font-medium text-foreground">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors cursor-pointer"
      >
        <span>{selected?.label || 'Select...'}</span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          {options?.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                if (opt.enterprise) { onEnterprise?.(); }
                else { onChange(opt.value); setOpen(false); }
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${
                opt.enterprise
                  ? 'text-gray-300 dark:text-gray-600 cursor-pointer hover:bg-muted/50'
                  : value === opt.value
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-foreground hover:bg-muted'
              }`}
            >
              <span>{opt.label}</span>
              {opt.enterprise && (
                <span className="text-[10px] font-semibold text-violet-500 bg-violet-500/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">Upgrade to Enterprise</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Test Call Popup ──────────────────────────────────────────────────────────
const TestCallPopup = ({ isOpen, onClose, agentId, variables = [] }) => {
  const [phone, setPhone] = useState('');
  const [placing, setPlacing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [varValues, setVarValues] = useState({});

  // Normalize: support both string and object formats, filter out mobile_number
  const displayVars = variables
    .map(v => typeof v === 'string' ? v : v.name)
    .filter(v => v !== 'mobile_number');

  useEffect(() => {
    if (isOpen && displayVars.length > 0) {
      const initial = {};
      displayVars.forEach(v => { initial[v] = ''; });
      setVarValues(initial);
    }
  }, [isOpen, variables]);

  const handlePlace = async () => {
    if (phone.length !== 10 || !agentId) return;
    setPlacing(true);
    setError(null);
    try {
      await api.post('/api/v1/platform-sessions', {
        agent_id: agentId,
        to_number: `+91${phone}`,
        direction: 'outbound',
        dynamic_vars: { ...varValues, mobile_number: `+91${phone}` },
      });
      setPlacing(false);
      setSuccess(true);
      if (typeof fbq === 'function') fbq('trackCustom', 'agentconfig-test-call-placed', { content_category: 'AgentConfigPage' });
      window.dataLayer?.push({ event: 'agentconfig_test_call_placed' });
      setTimeout(() => { setSuccess(false); setPhone(''); onClose(); }, 2000);
    } catch (err) {
      console.error('Test call failed:', err);
      setPlacing(false);
      setError(err.response?.data?.message || err.message || 'Failed to place test call');
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Phone" size={16} className="text-primary" />
            </div>
            <h3 className="text-base font-semibold text-foreground">Place a Test Call</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        </div>
        {success ? (
          <div className="flex items-center gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm font-medium text-success">Test call placed successfully!</p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Phone Number <span className="text-error">*</span></label>
              <div className="flex items-center gap-0">
                <span className="px-3 py-2.5 bg-muted border border-r-0 border-border rounded-l-lg text-sm font-medium text-foreground">+91</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e?.target?.value?.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10 digit number"
                  maxLength={10}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-r-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>
              <p className="text-xs text-muted-foreground">The agent will call this number for the test.</p>
            </div>
            {displayVars.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dynamic Variables</p>
                {displayVars.map((v) => (
                  <div key={v} className="space-y-1">
                    <label className="text-sm font-medium text-foreground capitalize">{v.replace(/_/g, ' ')}</label>
                    <input
                      type="text"
                      value={varValues[v] || ''}
                      onChange={(e) => setVarValues(prev => ({ ...prev, [v]: e.target.value }))}
                      placeholder={`Enter ${v.replace(/_/g, ' ')}...`}
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                ))}
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}
            <button
              onClick={handlePlace}
              disabled={phone.length !== 10 || placing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {placing ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="PhoneCall" size={16} />}
              {placing ? 'Placing Call...' : 'Place Call'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Template Library Panel ───────────────────────────────────────────────────
const TemplateLibraryPanel = ({ isOpen, onClose, onSelect }) => {
  const [activeIndustry, setActiveIndustry] = useState('All');
  const filtered = activeIndustry === 'All' ? PROMPT_TEMPLATES : PROMPT_TEMPLATES?.filter(t => t?.industry === activeIndustry);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[250] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div>
            <h3 className="text-base font-semibold text-foreground">Prompt Templates</h3>
            <p className="text-xs text-muted-foreground mt-0.5">20 industry templates</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        </div>
        <div className="flex gap-1.5 p-3 border-b border-border flex-wrap flex-shrink-0">
          {INDUSTRIES?.map(ind => (
            <button
              key={ind}
              onClick={() => setActiveIndustry(ind)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${activeIndustry === ind ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            >
              {ind}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filtered?.map((tpl, i) => (
            <button
              key={i}
              onClick={() => { onSelect(tpl?.prompt); onClose(); }}
              className="w-full text-left p-3 bg-background border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all group"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{tpl?.title}</p>
                <span className="text-xs px-1.5 py-0.5 bg-muted rounded text-muted-foreground flex-shrink-0">{tpl?.industry}</span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{tpl?.prompt}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Agent Tab ────────────────────────────────────────────────────────────────
const AgentTab = forwardRef(({ initialData }, ref) => {
  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [prompt, setPrompt] = useState('');
  const [hangupPrompt, setHangupPrompt] = useState('');
  const [hangupEnabled, setHangupEnabled] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    if (initialData) {
      setWelcomeMsg(initialData.greeting || '');
      setPrompt(initialData.instructions || '');
      setHangupPrompt(initialData.hangup_prompt || '');
      setHangupEnabled(!!initialData.hangup_prompt);
    }
  }, [initialData]);

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      greeting: welcomeMsg,
      instructions: prompt,
      hangup_prompt: hangupEnabled ? hangupPrompt : '',
    }),
  }));

  return (
    <div className="space-y-4">
      <SectionCard title="Agent Welcome Message">
        <TextareaField
          value={welcomeMsg}
          onChange={setWelcomeMsg}
          placeholder="Type the welcome message the agent will say when a call begins..."
          rows={2}
        />
      </SectionCard>

      <SectionCard
        title="Prompt"
        headerRight={
          <button
            onClick={() => setShowTemplates(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-xs font-medium transition-colors"
          >
            <Icon name="LayoutTemplate" size={14} />
            Template
          </button>
        }
      >
        <TextareaField
          value={prompt}
          onChange={setPrompt}
          placeholder="Define the agent's behavior, personality, and instructions..."
          rows={28}
        />
      </SectionCard>

      <SectionCard
        title="Hangup Using a Prompt"
        headerRight={
          <Toggle checked={hangupEnabled} onChange={setHangupEnabled} />
        }
      >
        <div className={`transition-opacity duration-200 ${hangupEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <TextareaField
            value={hangupPrompt}
            onChange={setHangupPrompt}
            placeholder="Define the condition or message that triggers the agent to hang up..."
            rows={3}
          />
        </div>
      </SectionCard>

      <TemplateLibraryPanel
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onSelect={(p) => setPrompt(p)}
      />
    </div>
  );
});

// ─── LLM Tab ──────────────────────────────────────────────────────────────────
const LLMTab = forwardRef(({ initialData }, ref) => {
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o');
  const [tokens, setTokens] = useState(500);
  const [temperature, setTemperature] = useState(0.7);
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [sectionFiles, setSectionFiles] = useState({});
  const [activeKBSections, setActiveKBSections] = useState([]);
  const [uploadingSectionId, setUploadingSectionId] = useState(null);
  const fileInputRef = useRef(null);

  const providerOptions = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'anthropic', label: 'Anthropic', enterprise: true },
    { value: 'google', label: 'Google Gemini', enterprise: true },
    { value: 'mistral', label: 'Mistral AI', enterprise: true },
    { value: 'cohere', label: 'Cohere', enterprise: true },
  ];
  const modelOptions = {
    openai: [{ value: 'gpt-4o', label: 'GPT-4o' }, { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' }, { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' }, { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }],
    anthropic: [{ value: 'claude-3-5-sonnet', label: 'Claude 3.5 Sonnet' }, { value: 'claude-3-haiku', label: 'Claude 3 Haiku' }],
    google: [{ value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' }, { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' }],
    mistral: [{ value: 'mistral-large', label: 'Mistral Large' }, { value: 'mistral-small', label: 'Mistral Small' }],
    cohere: [{ value: 'command-r-plus', label: 'Command R+' }, { value: 'command-r', label: 'Command R' }],
  };

  useEffect(() => {
    if (initialData?.llm_config) {
      const llm = initialData.llm_config;
      if (llm.provider) setProvider(llm.provider);
      if (llm.model) setModel(llm.model);
      if (llm.max_tokens != null) setTokens(llm.max_tokens);
      if (llm.temperature != null) setTemperature(llm.temperature);
    }
    if (initialData?.kbs?.length > 0) {
      const grouped = {};
      const activeSecs = [];
      const knownIds = KB_SECTIONS.map(k => k.id);
      initialData.kbs.forEach(kb => {
        const section = kb.section && knownIds.includes(kb.section) ? kb.section : 'unsorted';
        if (!grouped[section]) grouped[section] = [];
        grouped[section].push({ name: kb.name || 'Unknown', file: null, status: 'uploaded', publicUrl: kb.url });
        if (section !== 'unsorted' && !activeSecs.includes(section)) activeSecs.push(section);
      });
      setSectionFiles(grouped);
      setActiveKBSections(activeSecs);
    }
  }, [initialData]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files.length === 0 || !uploadingSectionId) return;
    const sectionId = uploadingSectionId;
    setUploadingSectionId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    const entries = files.map((f) => ({ name: f.name, file: f, status: 'uploading', publicUrl: null }));
    setSectionFiles((p) => ({ ...p, [sectionId]: [...(p[sectionId] || []), ...entries] }));

    const startIndex = (sectionFiles[sectionId] || []).length;
    await Promise.all(
      entries.map(async (entry, i) => {
        const idx = startIndex + i;
        try {
          const presignRes = await agentServiceApi.post('/storage/presigned-url', {
            file_name: entry.name,
            section: sectionId,
          });
          const { upload_url, public_url } = presignRes.data;
          await fetch(upload_url, {
            method: 'PUT',
            body: entry.file,
            headers: { 'Content-Type': entry.file.type || 'application/octet-stream' },
          });
          setSectionFiles((p) => {
            const arr = [...(p[sectionId] || [])];
            arr[idx] = { ...arr[idx], status: 'uploaded', publicUrl: public_url };
            return { ...p, [sectionId]: arr };
          });
        } catch (err) {
          console.error(`Upload failed for ${entry.name}:`, err);
          setSectionFiles((p) => {
            const arr = [...(p[sectionId] || [])];
            arr[idx] = { ...arr[idx], status: 'error' };
            return { ...p, [sectionId]: arr };
          });
        }
      })
    );
  };

  const toggleKBSection = (id) => {
    setActiveKBSections((p) => {
      if (p?.includes(id)) {
        setSectionFiles((f) => { const n = {...f}; delete n[id]; return n; });
        return p?.filter((x) => x !== id);
      }
      return [...p, id];
    });
  };

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      llm_config: { provider, model, max_tokens: tokens, temperature },
      kbs: Object.entries(sectionFiles).flatMap(([sectionId, files]) =>
        files.filter(f => f.status === 'uploaded' && f.publicUrl)
          .map(f => ({ url: f.publicUrl, section: sectionId, name: f.name }))
      ),
    }),
  }));

  return (
    <div className="space-y-4">
      <SectionCard title="Choose LLM Model">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProviderSelect label="Provider" value={provider} onChange={(v) => { setProvider(v); setModel(modelOptions?.[v]?.[0]?.value || ''); }} options={providerOptions} onEnterprise={() => setShowEnterprise(true)} />
          <SelectField label="Model" value={model} onChange={setModel} options={modelOptions?.[provider] || []} />
        </div>
      </SectionCard>
      <SectionCard title="Model Parameters">
        <div className="space-y-6">
          <SliderField
            label="Tokens Generated on Each LLM Output"
            min={0}
            max={1000}
            step={10}
            value={tokens}
            onChange={setTokens}
            description="Controls the maximum number of tokens the model generates per response."
          />
          <SliderField
            label="Temperature"
            min={0.01}
            max={1}
            step={0.01}
            value={temperature}
            onChange={setTemperature}
            description="Higher values make output more creative; lower values make it more focused."
          />
        </div>
      </SectionCard>
      <SectionCard title="Knowledge Base">
        <p className="text-xs text-muted-foreground mb-3">Manage knowledge sources for your assistant. Upload new files or remove existing ones.</p>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt,.csv,.xls,.xlsx"
          onChange={handleFileUpload}
          className="hidden"
        />

        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Sections</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {KB_SECTIONS?.map((sec) => {
            const isActive = activeKBSections?.includes(sec?.id);
            const fileName = sectionFiles[sec?.id];
            return (
              <div key={sec?.id} className="flex flex-col">
                <button
                  onClick={() => toggleKBSection(sec?.id)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-150 ${
                    fileName
                      ? 'border-violet-400 bg-violet-50 shadow-sm'
                      : isActive
                      ? 'border-violet-400 bg-violet-50 shadow-sm'
                      : 'border-gray-100 bg-white hover:border-violet-200'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive || fileName ? 'bg-violet-100' : 'bg-gray-100'}`}>
                    <Icon name={sec?.icon} size={15} className={isActive || fileName ? 'text-violet-600' : 'text-gray-500'} />
                  </div>
                  <span className={`text-xs font-medium text-center leading-tight ${isActive || fileName ? 'text-violet-700' : 'text-gray-600'}`}>{sec?.label}</span>
                </button>
                {isActive && (
                  <>
                    <button
                      onClick={() => { setUploadingSectionId(sec.id); fileInputRef.current?.click(); }}
                      className="mt-1.5 flex flex-col items-center justify-center gap-1 px-2 py-3 bg-gray-50 hover:bg-violet-50 rounded-lg border border-dashed border-gray-200 hover:border-violet-300 transition-colors"
                    >
                      <Icon name="Upload" size={14} className="text-gray-400" />
                      <span className="text-[10px] text-gray-500 font-medium">{fileName?.length > 0 ? 'Add more' : 'Upload docs'}</span>
                      <span className="text-[9px] text-gray-400">PDF, DOCX, TXT, CSV, XLS</span>
                    </button>
                    {fileName?.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {fileName.map((f, fi) => (
                          <div key={fi} className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${
                            f.status === 'error' ? 'bg-red-50 border-red-200' : 'bg-violet-50 border-violet-200'
                          }`}>
                            {f.status === 'uploading' ? (
                              <Icon name="Loader2" size={10} className="text-violet-500 animate-spin flex-shrink-0" />
                            ) : f.status === 'error' ? (
                              <Icon name="AlertCircle" size={10} className="text-red-500 flex-shrink-0" />
                            ) : (
                              <Icon name="CheckCircle2" size={10} className="text-green-500 flex-shrink-0" />
                            )}
                            {f.publicUrl ? (
                              <a href={f.publicUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] truncate flex-1 text-violet-700 hover:underline cursor-pointer">{f.name}</a>
                            ) : (
                              <span className={`text-[10px] truncate flex-1 ${f.status === 'error' ? 'text-red-600' : 'text-violet-700'}`}>{f.name}</span>
                            )}
                            {f.status !== 'uploading' && (
                              <button onClick={(e) => { e.stopPropagation(); setSectionFiles((p) => { const updated = (p[sec.id] || []).filter((_, j) => j !== fi); if (updated.length === 0) { const n = {...p}; delete n[sec.id]; return n; } return { ...p, [sec.id]: updated }; }); }} className="flex-shrink-0">
                                <Icon name="X" size={8} className="text-gray-400 hover:text-red-500" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        {sectionFiles['unsorted']?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Other Files</p>
            <div className="space-y-1">
              {sectionFiles['unsorted'].map((f, fi) => (
                <div key={fi} className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-violet-50 border-violet-200">
                  <Icon name="CheckCircle2" size={12} className="text-green-500 flex-shrink-0" />
                  <a href={f.publicUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-700 truncate flex-1 hover:underline">{f.name}</a>
                  <button onClick={() => { setSectionFiles((p) => { const updated = (p['unsorted'] || []).filter((_, j) => j !== fi); if (updated.length === 0) { const n = {...p}; delete n['unsorted']; return n; } return { ...p, unsorted: updated }; }); }} className="flex-shrink-0">
                    <Icon name="X" size={10} className="text-gray-400 hover:text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>
      <EnterpriseContactPopup isOpen={showEnterprise} onClose={() => setShowEnterprise(false)} />
    </div>
  );
});

// ─── Audio Tab ────────────────────────────────────────────────────────────────
const AudioTab = forwardRef(({ initialData }, ref) => {
  const [languages, setLanguages] = useState([{ value: 'hi', label: 'Hindi' }]);
  const [sttProvider, setSttProvider] = useState('soniox');
  const [sttModel, setSttModel] = useState('stt-rt-v4');
  const [keywords, setKeywords] = useState('');
  const [contextText, setContextText] = useState('');
  const [ttsProvider, setTtsProvider] = useState('smallest');
  const [ttsModel, setTtsModel] = useState('lightning_v3.1_pro');
  const [ttsVoice, setTtsVoice] = useState('meher');
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [apiVoices, setApiVoices] = useState([]);
  const [bufferSize, setBufferSize] = useState(100);
  const [speedRate, setSpeedRate] = useState(1.0);
  const [similarityBoost, setSimilarityBoost] = useState(0.75);
  const [stability, setStability] = useState(0.5);
  const [styleExaggeration, setStyleExaggeration] = useState(0.0);
  const [langDropOpen, setLangDropOpen] = useState(false);
  const langDropRef = useRef(null);

  const ENABLED_LANGUAGES = ['en', 'hi'];
  const allLanguages = [
    { value: 'hi', label: 'Hindi' }, { value: 'en', label: 'English' }, { value: 'ta', label: 'Tamil' },
    { value: 'te', label: 'Telugu' }, { value: 'kn', label: 'Kannada' }, { value: 'mr', label: 'Marathi' },
    { value: 'bn', label: 'Bengali' }, { value: 'gu', label: 'Gujarati' }, { value: 'pa', label: 'Punjabi' },
    { value: 'ml', label: 'Malayalam' }, { value: 'es', label: 'Spanish' }, { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' }, { value: 'ar', label: 'Arabic' }, { value: 'pt', label: 'Portuguese' },
  ];
  const sttProviders = [{ value: 'soniox', label: 'Soniox' }, { value: 'deepgram', label: 'Deepgram', enterprise: true }, { value: 'google', label: 'Google', enterprise: true }, { value: 'aws', label: 'AWS Transcribe', enterprise: true }, { value: 'azure', label: 'Azure', enterprise: true }];
  const sttModels = { soniox: [{ value: 'stt-rt-v4', label: 'STT RT v4' }], deepgram: [{ value: 'nova-2', label: 'Nova-2' }, { value: 'nova', label: 'Nova' }, { value: 'enhanced', label: 'Enhanced' }], google: [{ value: 'latest_long', label: 'Latest Long' }, { value: 'latest_short', label: 'Latest Short' }], aws: [{ value: 'default', label: 'Default' }], azure: [{ value: 'default', label: 'Default' }] };
  const ttsProviders = [{ value: 'smallest', label: 'Smallest AI' }, { value: 'elevenlabs', label: 'ElevenLabs', enterprise: true }, { value: 'google', label: 'Google', enterprise: true }, { value: 'aws', label: 'AWS Polly', enterprise: true }, { value: 'azure', label: 'Azure TTS', enterprise: true }];
  const ttsModels = { smallest: [{ value: 'lightning_v3.1_pro', label: 'Lightning v3.1 Pro' }, { value: 'lightning_v3.1', label: 'Lightning v3.1' }], elevenlabs: [{ value: 'eleven_multilingual_v2', label: 'Multilingual v2' }, { value: 'eleven_monolingual_v1', label: 'Monolingual v1' }], google: [{ value: 'standard', label: 'Standard' }, { value: 'wavenet', label: 'WaveNet' }], aws: [{ value: 'neural', label: 'Neural' }, { value: 'standard', label: 'Standard' }], azure: [{ value: 'neural', label: 'Neural' }] };
  const staticVoices = { elevenlabs: [{ value: 'rachel', label: 'Rachel (Female)' }, { value: 'adam', label: 'Adam (Male)' }, { value: 'bella', label: 'Bella (Female)' }, { value: 'josh', label: 'Josh (Male)' }], google: [{ value: 'en-US-Wavenet-A', label: 'Wavenet-A (Male)' }, { value: 'en-US-Wavenet-C', label: 'Wavenet-C (Female)' }], aws: [{ value: 'joanna', label: 'Joanna (Female)' }, { value: 'matthew', label: 'Matthew (Male)' }], azure: [{ value: 'aria', label: 'Aria (Female)' }, { value: 'guy', label: 'Guy (Male)' }] };
  const ttsVoices = { smallest: apiVoices.length > 0 ? apiVoices : [{ value: 'meher', label: 'Meher (Female)' }], ...staticVoices };

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const res = await agentServiceApi.get('/voices');
        const voiceList = res.data?.voices || res.data?.data?.voices || [];
        const mapped = voiceList
          .filter(v => v.is_active !== false)
          .map(v => ({ value: v.voice_id, label: `${v.display_name || v.voice_id} (${v.gender || '—'})` }));
        if (mapped.length > 0) {
          setApiVoices(mapped);
          if (!initialData?.tts_config?.voice) setTtsVoice(mapped[0].value);
        }
      } catch (err) {
        console.error('Failed to fetch voices:', err);
      }
    };
    fetchVoices();
  }, []);

  useEffect(() => {
    if (initialData) {
      // Load languages: prefer dedicated languages array, fallback to stt_config.language
      if (initialData.languages?.length > 0) {
        setLanguages(initialData.languages.map(l => ({ value: l.code, label: l.label })));
      } else if (initialData.stt_config?.language) {
        const lang = allLanguages.find(l => l.value === initialData.stt_config.language);
        if (lang) setLanguages([lang]);
      }

      const stt = initialData.stt_config;
      const tts = initialData.tts_config;
      if (stt) {
        if (stt.provider) setSttProvider(stt.provider);
        if (stt.model) setSttModel(stt.model);
        if (stt.context_terms?.length) setKeywords(stt.context_terms.join(', '));
        if (stt.context_text) setContextText(stt.context_text);
      }
      if (tts) {
        if (tts.provider) setTtsProvider(tts.provider);
        if (tts.model) setTtsModel(tts.model);
        if (tts.voice) setTtsVoice(tts.voice);
        if (tts.speed != null) setSpeedRate(tts.speed);
        if (tts.stability != null) setStability(tts.stability);
        if (tts.similarity_boost != null) setSimilarityBoost(tts.similarity_boost);
      }
    }
  }, [initialData]);

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      stt_config: { provider: sttProvider, model: sttModel, language: languages?.[0]?.value || 'en', ...(keywords ? { context_terms: keywords.split(',').map(k => k.trim()).filter(Boolean) } : {}), ...(contextText ? { context_text: contextText } : {}) },
      tts_config: { provider: ttsProvider, model: ttsModel, voice: ttsVoice, speed: speedRate, stability, similarity_boost: similarityBoost, volume: 0.75, language: languages?.[0]?.value || 'en' },
      languages: languages.map((l, i) => ({ code: l.value, label: l.label, is_primary: i === 0 })),
    }),
  }));

  // Close language dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (langDropRef.current && !langDropRef.current.contains(e.target)) setLangDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLangSelect = (langValue) => {
    if (!ENABLED_LANGUAGES.includes(langValue)) {
      setShowEnterprise(true);
      setLangDropOpen(false);
      return;
    }
    if (!languages?.find(l => l?.value === langValue)) {
      const lang = allLanguages?.find(l => l?.value === langValue);
      if (lang) setLanguages(prev => [...prev, lang]);
    }
    setLangDropOpen(false);
  };

  const removeLanguage = (val) => setLanguages(prev => prev?.filter(l => l?.value !== val));

  return (
    <div className="space-y-4">
      <SectionCard title="Language">
        <div className="flex flex-wrap gap-2 mb-3">
          {languages?.map(lang => (
            <span key={lang?.value} className="flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
              {lang?.label}
              {languages?.length > 1 && (
                <button onClick={() => removeLanguage(lang?.value)} className="hover:text-error transition-colors">
                  <Icon name="X" size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
        <div className="relative" ref={langDropRef}>
          <button
            type="button"
            onClick={() => setLangDropOpen(!langDropOpen)}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors cursor-pointer"
          >
            <span className="text-muted-foreground">Add language...</span>
            <Icon name={langDropOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground" />
          </button>
          {langDropOpen && (
            <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg overflow-hidden max-h-56 overflow-y-auto">
              {allLanguages?.filter(l => !languages?.find(sl => sl?.value === l?.value))?.map((l) => {
                const isEnabled = ENABLED_LANGUAGES.includes(l.value);
                return (
                  <button
                    key={l.value}
                    type="button"
                    onClick={() => handleLangSelect(l.value)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${
                      isEnabled
                        ? 'text-foreground hover:bg-muted'
                        : 'text-gray-300 dark:text-gray-600 cursor-pointer hover:bg-muted/50'
                    }`}
                  >
                    <span>{l.label}</span>
                    {!isEnabled && (
                      <span className="text-[10px] font-semibold text-violet-500 bg-violet-500/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">Upgrade to Enterprise</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SectionCard>
      <SectionCard title="Speech-to-Text">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProviderSelect label="Provider" value={sttProvider} onChange={(v) => { setSttProvider(v); setSttModel(sttModels?.[v]?.[0]?.value || ''); }} options={sttProviders} onEnterprise={() => setShowEnterprise(true)} />
          <SelectField label="Model" value={sttModel} onChange={setSttModel} options={sttModels?.[sttProvider] || []} />
        </div>
      </SectionCard>
      <SectionCard title="Keywords">
        <TextareaField
          value={keywords}
          onChange={setKeywords}
          placeholder="Enter keywords separated by commas (e.g., stop, cancel, refund, help)..."
          rows={3}
        />
        <p className="text-xs text-muted-foreground">Keywords help the agent recognize specific terms during conversation.</p>
      </SectionCard>
      <SectionCard title="Background Instructions">
        <TextareaField
          value={contextText}
          onChange={setContextText}
          placeholder="Describe the call context to improve speech recognition (e.g., 'This is a real estate agency. Common terms: EMI, carpet area, registry, stamp duty...')"
          rows={4}
        />
        <p className="text-xs text-muted-foreground">Provide background context to help the speech engine better understand domain-specific terms and conversation flow.</p>
      </SectionCard>
      <SectionCard title="Text-to-Speech">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <ProviderSelect label="Provider" value={ttsProvider} onChange={(v) => { setTtsProvider(v); setTtsModel(ttsModels?.[v]?.[0]?.value || ''); setTtsVoice(ttsVoices?.[v]?.[0]?.value || ''); }} options={ttsProviders} onEnterprise={() => setShowEnterprise(true)} />
          <SelectField label="Model" value={ttsModel} onChange={setTtsModel} options={ttsModels?.[ttsProvider] || []} />
          <SelectField label="Voice" value={ttsVoice} onChange={setTtsVoice} options={ttsVoices?.[ttsProvider] || []} />
        </div>
        <div className="space-y-5">
          <SliderField label="Speed Rate" min={0.7} max={1.2} step={0.01} value={speedRate} onChange={setSpeedRate} description="Adjust the speaking speed of the voice." />
          {ttsProvider !== 'smallest' && (
            <>
              <SliderField label="Buffer Size" min={1} max={400} step={1} value={bufferSize} onChange={setBufferSize} description="Controls audio buffer size in milliseconds." />
              <SliderField label="Similarity Boost" min={0} max={1} step={0.01} value={similarityBoost} onChange={setSimilarityBoost} description="Higher values make the voice more similar to the original." />
              <SliderField label="Stability" min={0} max={1} step={0.01} value={stability} onChange={setStability} description="Higher stability reduces variability in the voice output." />
              <SliderField label="Style Exaggeration" min={0} max={1} step={0.01} value={styleExaggeration} onChange={setStyleExaggeration} description="Amplifies the speaking style of the selected voice." />
            </>
          )}
        </div>
      </SectionCard>
      <EnterpriseContactPopup isOpen={showEnterprise} onClose={() => setShowEnterprise(false)} />
    </div>
  );
});

// ─── Engine Tab ───────────────────────────────────────────────────────────────
const EngineTab = forwardRef(({ initialData }, ref) => {
  const [interruptWords, setInterruptWords] = useState(3);
  const [userOnlineEnabled, setUserOnlineEnabled] = useState(false);
  const [onlineMessage, setOnlineMessage] = useState('');
  const [invokeAfter, setInvokeAfter] = useState(10);

  useEffect(() => {
    if (initialData) {
      const intr = initialData.interruption_config;
      const sess = initialData.session_config;
      if (intr?.min_words != null) setInterruptWords(intr.min_words);
      if (sess?.user_online_enabled != null) setUserOnlineEnabled(sess.user_online_enabled);
      if (sess?.user_online_message) setOnlineMessage(sess.user_online_message);
      if (sess?.inactivity_timeout != null) setInvokeAfter(sess.inactivity_timeout);
    }
  }, [initialData]);

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      interruption_config: { enabled: true, min_words: interruptWords },
      session_config_partial: {
        inactivity_timeout: invokeAfter,
        user_online_enabled: userOnlineEnabled,
        ...(onlineMessage ? { user_online_message: onlineMessage } : {}),
      },
    }),
  }));

  return (
    <div className="space-y-4">
      <SectionCard title="Transcription & Interruptions">
        <SliderField
          label="Number of Words to Wait Before Interrupting"
          min={0}
          max={10}
          step={1}
          value={interruptWords}
          onChange={setInterruptWords}
        />
        <div className="mt-3 p-3 bg-muted/60 rounded-lg border border-border space-y-2">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={14} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              Agent will not consider interruptions until <span className="font-semibold text-foreground">{interruptWords} word{interruptWords !== 1 ? 's' : ''}</span> are spoken.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="AlertCircle" size={14} className="text-warning mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              If recipient says <span className="font-semibold text-foreground">"Stopwords"</span> such as <em>Stop, Wait, Hold On</em>, agent will pause by default.
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="User Online Detection"
        headerRight={<Toggle checked={userOnlineEnabled} onChange={setUserOnlineEnabled} />}
      >
        <div className={`space-y-4 transition-opacity duration-200 ${userOnlineEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <TextareaField
            label="Detection Message"
            value={onlineMessage}
            onChange={setOnlineMessage}
            placeholder="Message to invoke when user online detection triggers..."
            rows={3}
          />
          <SliderField
            label="Invoke Message After (seconds)"
            min={5}
            max={20}
            step={1}
            value={invokeAfter}
            onChange={setInvokeAfter}
            unit="s"
            description="Time in seconds before the detection message is triggered."
          />
        </div>
      </SectionCard>
    </div>
  );
});

// ─── Call Tab ─────────────────────────────────────────────────────────────────
const CallTab = forwardRef(({ initialData }, ref) => {
  const [telephonyProvider, setTelephonyProvider] = useState('plivo');
  const [ambientNoise, setAmbientNoise] = useState('none');
  const [noiseCancellation, setNoiseCancellation] = useState(true);
  const [voicemailDetection, setVoicemailDetection] = useState(false);
  const [thinkingSound, setThinkingSound] = useState(true);
  const [dtmf] = useState(false);
  const [autoReschedule] = useState(false);
  const [outboundEnabled] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [finalMessage, setFinalMessage] = useState('');
  const [hangupOnSilence, setHangupOnSilence] = useState(false);
  const [silenceTimeout, setSilenceTimeout] = useState(10);
  const [callTimeout, setCallTimeout] = useState(300);
  const [greetingDelay, setGreetingDelay] = useState(1.0);
  const [showEnterprise, setShowEnterprise] = useState(false);

  useEffect(() => {
    if (initialData?.session_config) {
      const sess = initialData.session_config;
      if (sess.max_duration != null) setCallTimeout(sess.max_duration);
      if (sess.silence_hangup_enabled != null) setHangupOnSilence(sess.silence_hangup_enabled);
      if (sess.silence_hangup_timeout != null) setSilenceTimeout(sess.silence_hangup_timeout);
      if (sess.greeting_delay != null) setGreetingDelay(sess.greeting_delay);
    }
    if (initialData?.audio_config) {
      const ac = initialData.audio_config;
      if (ac.bns_type) setAmbientNoise(ac.bns_type);
      else if (ac.bns_enabled) setAmbientNoise('office');
      else setAmbientNoise('none');
      if (ac.nc_mode) setNoiseCancellation(ac.nc_mode !== 'off');
      if (ac.voicemail_detection != null) setVoicemailDetection(ac.voicemail_detection);
      if (ac.thinking_sound != null) setThinkingSound(ac.thinking_sound);
    }
    if (initialData?.telephony_config?.provider) setTelephonyProvider(initialData.telephony_config.provider);
  }, [initialData]);

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      session_config: {
        max_duration: callTimeout,
        greeting_delay: greetingDelay,
        silence_hangup_enabled: hangupOnSilence,
        silence_hangup_timeout: silenceTimeout,
      },
      audio_config: {
        bns_enabled: ambientNoise !== 'none',
        bns_type: ambientNoise,
        nc_mode: noiseCancellation ? 'telephony' : 'off',
        voicemail_detection: voicemailDetection,
        thinking_sound: thinkingSound,
      },
      telephony_config: {
        provider: telephonyProvider,
      },
    }),
  }));

  const telephonyOptions = [
    { value: 'plivo', label: 'Plivo' },
    { value: 'vobiz', label: 'Vobiz' },
    { value: 'twilio', label: 'Twilio', enterprise: true },
    { value: 'vonage', label: 'Vonage', enterprise: true },
    { value: 'exotel', label: 'Exotel', enterprise: true },
    { value: 'tata', label: 'Tata Tele', enterprise: true },
  ];
  const noiseOptions = [
    { value: 'none', label: 'None (Disabled)' },
    { value: 'office', label: 'Office' },
    { value: 'city', label: 'City' },
    { value: 'forest', label: 'Forest' },
  ];
  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const h = String(i)?.padStart(2, '0');
    return [{ value: `${h}:00`, label: `${h}:00` }, { value: `${h}:30`, label: `${h}:30` }];
  })?.flat();

  return (
    <div className="space-y-4">
      <SectionCard title="Call Configuration">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <ProviderSelect label="Telephony Provider" value={telephonyProvider} onChange={setTelephonyProvider} options={telephonyOptions} onEnterprise={() => setShowEnterprise(true)} />
          <SelectField label="Ambient Noise" value={ambientNoise} onChange={setAmbientNoise} options={noiseOptions} />
        </div>
        <div className="space-y-3 pt-2 border-t border-border">
          <Toggle label="Noise Cancellation" description="Reduce background noise during calls" checked={noiseCancellation} onChange={setNoiseCancellation} />
          <Toggle label="Voicemail Detection" description="Automatically detect and handle voicemail" checked={voicemailDetection} onChange={setVoicemailDetection} />
          <Toggle label="Thinking Sound" description="Play keyboard typing sound while agent is processing" checked={thinkingSound} onChange={setThinkingSound} />
          <Toggle label="Keypad Input (DTMF)" description="Allow users to interact via keypad tones" checked={dtmf} onChange={() => setShowEnterprise(true)} />
          <Toggle label="Auto Reschedule" description="Automatically reschedule failed call attempts" checked={autoReschedule} onChange={() => setShowEnterprise(true)} />
        </div>
      </SectionCard>
      <SectionCard
        title="Outbound Call Timing Restrictions"
        headerRight={<Toggle checked={outboundEnabled} onChange={() => setShowEnterprise(true)} />}
      >
        <div className={`space-y-4 transition-opacity duration-200 ${outboundEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <p className="text-sm text-muted-foreground">Allowed Time Window</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Start Time</label>
              <select
                value={startTime}
                onChange={(e) => setStartTime(e?.target?.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary appearance-none"
              >
                {timeOptions?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">End Time</label>
              <select
                value={endTime}
                onChange={(e) => setEndTime(e?.target?.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary appearance-none"
              >
                {timeOptions?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      </SectionCard>
      <SectionCard title="Final Call Message">
        <TextareaField
          value={finalMessage}
          onChange={setFinalMessage}
          placeholder="Message the agent will say before ending the call..."
          rows={3}
        />
      </SectionCard>
      <SectionCard title="Call Management">
        <div className="space-y-4">
          <div className="space-y-3">
            <Toggle
              label="Hangup on User Silence"
              description="End the call if the user is silent for the specified duration"
              checked={hangupOnSilence}
              onChange={setHangupOnSilence}
            />
            <div className={`transition-opacity duration-200 ${hangupOnSilence ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
              <SliderField
                label="Silence Timeout"
                min={1}
                max={30}
                step={1}
                value={silenceTimeout}
                onChange={setSilenceTimeout}
                unit="s"
              />
            </div>
          </div>
          <div className="pt-2 border-t border-border">
            <SliderField
              label="Greeting Delay"
              min={0}
              max={5}
              step={0.1}
              value={greetingDelay}
              onChange={setGreetingDelay}
              unit="s"
              description="Seconds to wait before the agent speaks its greeting."
            />
          </div>
          <div className="pt-2 border-t border-border">
            <SliderField
              label="Total Call Timeout"
              min={30}
              max={2400}
              step={30}
              value={callTimeout}
              onChange={setCallTimeout}
              unit="s"
              description="Maximum duration for a single call session."
            />
          </div>
        </div>
      </SectionCard>
      <EnterpriseContactPopup isOpen={showEnterprise} onClose={() => setShowEnterprise(false)} />
    </div>
  );
});

// ─── Main AgentConfigPage ─────────────────────────────────────────────────────
const TABS = [
  { id: 'agent', label: 'Agent', icon: 'Bot' },
  { id: 'llm', label: 'LLM', icon: 'Brain' },
  { id: 'audio', label: 'Audio', icon: 'Mic' },
  { id: 'engine', label: 'Engine', icon: 'Settings2' },
  { id: 'call', label: 'Call', icon: 'Phone' },
];

const AgentConfigPage = ({ agent, onBack, isOpen }) => {
  const [agentName, setAgentName] = useState(agent?.name || 'Unnamed Agent');
  const [activeTab, setActiveTab] = useState('agent');
  const [showTestCall, setShowTestCall] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [configData, setConfigData] = useState(null);

  const nameInputRef = useRef(null);
  const agentTabRef = useRef(null);
  const llmTabRef = useRef(null);
  const audioTabRef = useRef(null);
  const engineTabRef = useRef(null);
  const callTabRef = useRef(null);

  // Load config from agent-service on open
  useEffect(() => {
    if (isOpen && agent?.id) {
      const loadConfig = async () => {
        setLoading(true);
        setLoadError(null);
        try {
          const res = await agentServiceApi.get(`/agents/${agent.id}`);
          const data = res.data?.data || res.data;
          setConfigData(data);
          if (data.name) setAgentName(data.name);
        } catch (err) {
          console.error('Failed to load agent config:', err);
          setLoadError('Failed to load config from agent-service');
        } finally {
          setLoading(false);
        }
      };
      loadConfig();
    }
  }, [isOpen, agent?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const agentValues = agentTabRef.current?.getValues() || {};
      const llmValues = llmTabRef.current?.getValues() || {};
      const audioValues = audioTabRef.current?.getValues() || {};
      const engineValues = engineTabRef.current?.getValues() || {};
      const callValues = callTabRef.current?.getValues() || {};

      const payload = {
        name: agentName,
        ...agentValues,
        ...llmValues,
        ...audioValues,
        interruption_config: engineValues.interruption_config,
        audio_config: callValues.audio_config,
        telephony_config: callValues.telephony_config,
        session_config: {
          ...callValues.session_config,
          ...engineValues.session_config_partial,
        },
      };

      await agentServiceApi.put(`/agents/${agent.id}`, payload);

      // Re-fetch agent config so UI reflects server-side changes (e.g. regenerated instructions)
      try {
        const res = await agentServiceApi.get(`/agents/${agent.id}`);
        const freshData = res.data?.data || res.data;
        setConfigData(freshData);
        if (freshData.name) setAgentName(freshData.name);
      } catch (_) { /* ignore refetch errors */ }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save agent config failed:', err);
      alert('Failed to save agent config: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[150] flex justify-end transition-all duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={onBack}
      />
      {/* Drawer Panel */}
      <div
        className={`relative w-full max-w-3xl bg-background flex flex-col overflow-hidden shadow-2xl transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {showChat ? (
          <AgentTestChatScreen
            agent={agent}
            agentName={agentName}
            onBack={() => setShowChat(false)}
            isOpen={showChat}
          />
        ) : (
          <>
            {/* Fixed Top Bar */}
            <div className="flex-shrink-0 bg-card border-b border-border px-4 md:px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 shadow-sm">
              {/* Row 1 on mobile: name + save | On desktop: all in one row */}
              <div className="flex items-center justify-between sm:justify-start gap-3 min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={onBack}
                    className="p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
                    title="Close panel"
                  >
                    <Icon name="X" size={18} className="text-foreground" />
                  </button>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Bot" size={16} className="text-primary" />
                    </div>
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={agentName}
                      onChange={(e) => setAgentName(e?.target?.value)}
                      className="text-base md:text-lg font-semibold text-foreground bg-transparent border-b-2 border-transparent hover:border-border focus:border-primary focus:outline-none transition-colors min-w-0 w-full max-w-[140px] sm:max-w-xs"
                      title="Click to edit agent name"
                    />
                    <button onClick={() => nameInputRef.current?.focus()} className="flex-shrink-0 p-1 rounded hover:bg-muted transition-colors" title="Edit name">
                      <Icon name="Pencil" size={13} className="text-muted-foreground" />
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`flex sm:hidden items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${saved ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {saving ? <Icon name="Loader2" size={15} className="animate-spin" /> : saved ? <Icon name="Check" size={15} /> : <Icon name="Save" size={15} />}
                  <span>{saving ? '...' : saved ? '' : 'Save'}</span>
                </button>
              </div>

              {/* Row 2 on mobile: test call + chat (left-aligned) | On desktop: test call + chat + save */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => { if (typeof fbq === 'function') fbq('trackCustom', 'agentconfig-test-call-clicked', { content_category: 'AgentConfigPage' }); window.dataLayer?.push({ event: 'agentconfig_test_call_clicked' }); setShowTestCall(true); }}
                  className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm font-medium transition-colors"
                >
                  <Icon name="PhoneCall" size={15} />
                  <span className="hidden sm:inline">Place a Test Call</span>
                  <span className="sm:hidden">Test Call</span>
                </button>
                <button
                  onClick={() => { if (typeof fbq === 'function') fbq('trackCustom', 'agentconfig-chat-with-agent-clicked', { content_category: 'AgentConfigPage' }); window.dataLayer?.push({ event: 'agentconfig_chat_with_agent_clicked' }); setShowChat(true); }}
                  className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm font-medium transition-colors"
                >
                  <Icon name="MessageSquare" size={15} />
                  <span className="hidden sm:inline">Chat With Agent</span>
                  <span className="sm:hidden">Chat</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${saved ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {saving ? <Icon name="Loader2" size={15} className="animate-spin" /> : saved ? <Icon name="Check" size={15} /> : <Icon name="Save" size={15} />}
                  <span>{saving ? 'Saving...' : saved ? 'Saved!' : 'Save Agent'}</span>
                </button>
              </div>
            </div>
            {/* Tab Navigation */}
            <div className="flex-shrink-0 bg-card border-b border-border px-2 sm:px-4 md:px-6 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex gap-0 min-w-max">
                {TABS?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={15} />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 md:px-6 py-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Icon name="Loader2" size={28} className="animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Loading agent config...</p>
                  </div>
                ) : loadError ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Icon name="AlertCircle" size={28} className="text-red-500" />
                    <p className="text-sm text-red-500">{loadError}</p>
                  </div>
                ) : (
                  <>
                    {activeTab === 'agent' && <AgentTab ref={agentTabRef} initialData={configData} />}
                    {activeTab === 'llm' && <LLMTab ref={llmTabRef} initialData={configData} />}
                    {activeTab === 'audio' && <AudioTab ref={audioTabRef} initialData={configData} />}
                    {activeTab === 'engine' && <EngineTab ref={engineTabRef} initialData={configData} />}
                    {activeTab === 'call' && <CallTab ref={callTabRef} initialData={configData} />}
                  </>
                )}
              </div>
            </div>
            <TestCallPopup isOpen={showTestCall} onClose={() => setShowTestCall(false)} agentId={agent?.id} variables={configData?.variables || []} />
          </>
        )}
      </div>
    </div>
  );
};

export default AgentConfigPage;
